import React, { useMemo, useRef, useState } from "react";

import {
  useReactTable,
  getCoreRowModel,
  flexRender
} from "@tanstack/react-table";

import { TableWrapper } from "components/Tables/styled";

import LoadingSpinner from "components/PageLoader/Spinner";
import Pagination from "components/Pagination/index";
import EmptyData from "components/EmptyData/index";

import {
  ArrowDownStrokeRegularIcon,
  CaretUpDownStrokeRegularIcon
} from "assets/SVGComponents/index";

const CommonTable = ({
  data = [],
  columns = [],
  page,
  limit,
  total_count,
  onPageChange,
  loading = false,
  showEditModal,
  handleCloseModal,
  selectedRow,
  currentStatus,
  initialLoading,
  onSortChange,
  sort_by,
  sort_order,
  tableClassName,
  tableWrapperStyles
}) => {
  // === COLUMN RESIZING STATE ===
  const [columnSizing, setColumnSizing] = useState({});
  const [hoveredColumn, setHoveredColumn] = useState(null);
  const [userHasSorted, setUserHasSorted] = useState(false);

  const tableContainerRef = useRef(null);

  // === TANSTACK TABLE INSTANCE ===
  const table = useReactTable({
    data,
    columns,
    state: { columnSizing },
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: "onChange"
  });

  // === Generate CSS variables for live resizing ===
  const columnSizeVars = useMemo(() => {
    const vars = {};
    table.getFlatHeaders().forEach((header) => {
      vars[`--col-${header.id}-size`] = `${header.getSize()}`;
    });
    return vars;
  }, [table, columnSizing, columns]);

  const isEmpty = !loading && data.length === 0;

  return (
    <>
      <TableWrapper ref={tableContainerRef}>
        <div
          className={`${tableClassName} ${loading ? "scroll-container loading-container" : "scroll-container"}`}
        >
          {loading ? (
            <LoadingSpinner
              color="#797979"
              text="Loading Data..."
              fontSize={16}
            />
          ) : isEmpty ? (
            <EmptyData />
          ) : (
            <table
              style={{ ...columnSizeVars, width: table.getTotalSize() }}
              className={tableWrapperStyles}
            >
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => {
                      const headerClass =
                        header.column.columnDef.headerClassName || "";

                      return (
                        <th
                          key={header.id}
                          style={{
                            width: `calc(var(--col-${header.id}-size) * 1px)`,
                            maxWidth: `calc(var(--col-${header.id}-size) * 1px)`,
                            position: "relative",
                            cursor: header.column.getCanSort()
                              ? "pointer"
                              : "default"
                          }}
                          className={`${index === 0 ? "table__head-cell--sticky" : ""} ${headerClass}`}
                          onMouseEnter={() => setHoveredColumn(header.id)}
                          onMouseLeave={() => setHoveredColumn(null)}
                          onClick={(e) => {
                            if (!header.column.getCanSort()) return;
                            e.stopPropagation();
                            const newOrder =
                              sort_by === header.id && sort_order === "asc"
                                ? "desc"
                                : "asc";
                            setUserHasSorted(true);
                            onSortChange?.(header.id, newOrder);
                          }}
                        >
                          {header.isPlaceholder ? null : (
                            <div
                              className={`${
                                headerClass?.trim() === "table-header" ||
                                headerClass?.trim() === "table-header mono" ||
                                header.column.getCanSort()
                                  ? "header-content"
                                  : "header-content-reverse"
                              } ${headerClass?.trim() === "table-header mono" ? "mono" : ""}`}
                            >
                              <div className="truncate-text">
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </div>

                              {header.column.getCanSort() && (
                                <div className="sort-icon">
                                  {/* When column is active (sorted) — show arrow */}
                                  {sort_by === header.id && userHasSorted ? (
                                    <ArrowDownStrokeRegularIcon
                                      style={{
                                        transition: "transform 0.2s ease",
                                        transform:
                                          sort_order === "asc"
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)"
                                      }}
                                      className="active"
                                    />
                                  ) : (
                                    /* When column is hovered (and not active) — show caret */
                                    hoveredColumn === header.id && (
                                      <CaretUpDownStrokeRegularIcon
                                        style={{
                                          opacity: 0.6,
                                          transition: "opacity 0.2s ease"
                                        }}
                                      />
                                    )
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {header.column.getCanResize() &&
                            index !== headerGroup.headers.length - 1 && (
                              <div
                                className={`table__resize-handle ${
                                  header.column.getIsResizing()
                                    ? "isResizing"
                                    : ""
                                }`}
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                              />
                            )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell, i) => {
                      const cellClass =
                        cell.column.columnDef.cellClassName || "";

                      return (
                        <td
                          key={cell.id}
                          style={{
                            width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
                            maxWidth: `calc(var(--col-${cell.column.id}-size) * 1px)`
                          }}
                          className={`${i === 0 ? "table__body-cell--sticky" : ""} ${cellClass}`}
                          onMouseEnter={() => setHoveredColumn(cell.column.id)}
                          onMouseLeave={() => setHoveredColumn(null)}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </TableWrapper>

      {!isEmpty && (
        <Pagination
          page={page}
          limit={limit}
          totalCount={total_count}
          onPageChange={onPageChange}
          initialLoading={initialLoading}
        />
      )}

      {/* <UpdateRosterStatusModal
        show={showEditModal}
        onHide={handleCloseModal}
        rowData={selectedRow}
        currentStatus={currentStatus}
      /> */}
    </>
  );
};

export default CommonTable;
